/**
 * Meeting a train, from the driver's side.
 *
 * Pure by design so every rule here is testable in node: which train Eben is likely on, when
 * David has to leave, and whether he will be early or late. Nothing in this file talks to the
 * network, reads a clock of its own, or knows what a React component is.
 *
 * The one asymmetry worth stating out loud: guessing the earlier train costs David a short wait
 * at the curb, and guessing the later one leaves Eben on the street for ten minutes. So every
 * judgement call in here leans early.
 */

import type { CommutePlan, DriverProgress, PickupAckStep, PickupReport, PickupStage, PickupState, PickupStatus, PickupTrain, RiderPhase } from "../shared/types";
import { headsignLabel, stationLabel } from "../shared/labels";
import { epochAtMinutes } from "../shared/time";

/**
 * How far outside the College Prep fence a fix has to put him before he counts as gone.
 *
 * A guess, and deliberately a small one — every crossing is logged so it can be replaced with a
 * measurement. The accuracy rule matters more than the radius: a vague fix cannot claim he has
 * left, the same discipline the arrival fences already use.
 */
export const SCHOOL_FENCE_METERS = 150;

/**
 * The daytime floor for private planning, as minutes past local midnight.
 *
 * Earlier planning always reaches this far for coordination. Later planning extends from the
 * current clock instead, so late pickups and testing are not cut off by a fixed afternoon time.
 */
export const PICKUP_PLANNING_UNTIL_MINUTES = 17 * 60 + 30;
export const PICKUP_PLANNING_LOOKAHEAD_MS = 2 * 60 * 60_000;
export const PICKUP_ACTIVITY_HORIZON_MS = 2 * 60 * 60_000;

export function planningCountdown(totalMinutes: number): { value: number; unit: string; remainder: string | null } {
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return hours > 0
		? { value: hours, unit: hours === 1 ? "hour" : "hours", remainder: minutes ? `${minutes} ${minutes === 1 ? "minute" : "minutes"}` : null }
		: { value: totalMinutes, unit: totalMinutes === 1 ? "minute" : "minutes", remainder: null };
}

/** A planning CTA earns lock-screen space only when David may need it within two hours. */
export function pickupActivityWithinHorizon(startByMs: number | null, nowMs: number): boolean {
	return startByMs != null && startByMs - nowMs <= PICKUP_ACTIVITY_HORIZON_MS;
}

/** 5:30 is the coordination floor; later on, always keep two hours of departures visible. */
export function pickupPlanningCutoffMs(nowMs: number, timezone: string, floorMinutes = PICKUP_PLANNING_UNTIL_MINUTES): number {
	return Math.max(epochAtMinutes(nowMs, floorMinutes, timezone), nowMs + PICKUP_PLANNING_LOOKAHEAD_MS);
}

/** En-route trains remain useful until Civic Center; future trains are bounded at Rockridge. */
export function pickupPlanningOptions(options: CommutePlan[], destinationIndex: number, nowMs: number, cutoffMs: number): CommutePlan[] {
	return options.filter((plan) => {
		const destination = plan.destinations[destinationIndex] ?? plan.destinations[0] ?? null;
		return destination != null && destination.predictedMs > nowMs && plan.boarding.predictedMs <= cutoffMs;
	});
}

/**
 * Train arrival to curb.
 *
 * Measured once, on 2026-08-27 at Civic Center: realtime arrival 16:37:09, he pressed off-train
 * 16:37:24, and the first clean street fix — 32 m from the Hyde St pickup point — was 16:40:09.
 * That is 2 min 45 s to 3 min. Rounded down to two, because arriving early is the cheap mistake.
 *
 * The doors opening cannot be measured: accuracy underground was 1414 m.
 */
export const PLATFORM_TO_CURB_SECONDS = 120;

/**
 * How fast he covers the ground between school and the platform.
 *
 * Between a walk (1.6 m/s observed) and the shuttle (8.9 m/s observed), and chosen at the fast
 * end on purpose: this decides whether the *earlier* train is still on, and the whole point is
 * to lean that way. At 500 m out it allows just over two minutes, which is the case the rule was
 * described by.
 */
const APPROACH_SPEED_METERS_PER_SECOND = 4;

/** How long Eben's section keeps saying "request sent" before it gives up and hides itself. */
export const OFFER_GRACE_MS = 60_000;

/**
 * The kerb David drives to.
 *
 * Hardcoded the same way the two roles are, and for the same reason: this is a feature for one
 * family, and the pickup has only ever happened at Civic Center. It applies only once David has
 * *entered* — until then the ride home is Eben's to route, and he may legitimately be going to Glen
 * Park. Entering is the fact that settles it, because he may not have touched the app at all.
 */
export const PICKUP_STATION_ID = "CIVC";

/** Whether the driver has committed, which is what makes the pickup station his answer not Eben's. */
export function pickupSettled(pickup: { enteredAtMs: number | null; status: PickupStatus } | null): boolean {
	return pickup != null && pickup.enteredAtMs != null && pickup.status === "active";
}

/**
 * Whether the rider's own choice of stop still stands.
 *
 * The kerb overrides a station Eben never picked — that is what `pickupSettled` is for. It must not
 * override one he did. The picker on the card is dated, so the two cases are distinguishable
 * without guessing at intent: a tap after David entered is a change of mind, and it wins.
 */
export function riderChoseStation(
	pickup: { enteredAtMs: number | null; status: PickupStatus } | null,
	chosenAtMs: number | null,
): boolean {
	return chosenAtMs != null && (!pickupSettled(pickup) || chosenAtMs > (pickup?.enteredAtMs ?? 0));
}

/**
 * How far from the meeting point still counts as parked at it.
 *
 * Generous, because "the spot" is a stretch of kerb and a fix has noise — but nowhere near
 * generous enough to cover having driven off.
 */
export const PICKUP_POINT_METERS = 250;

/** Only the request that atomically moved the driver to the kerb may notify the rider. */
export function claimedRiderArrival(action: string, changedRows: number): boolean {
	return action === "here" && changedRows === 1;
}

/** Entering starts a fresh drive, but reopening the shared screen never rewinds it. */
export function driverProgressForAction(action: string, current: DriverProgress): DriverProgress {
	if (action === "enter") {
		return current === "not_started" ? "preparing" : current;
	}
	if (action === "driving") return "driving";
	if (action === "here") return "at_pickup_point";
	if (action === "done") return "picked_up";
	return current;
}

/**
 * Where a fix leaves the driver, given what he last said he was doing.
 *
 * "I'm here" is a claim about being somewhere, and driving away unsays it. Without this the tap
 * latched: the card read "parked and waiting" from seven kilometres out, and Eben's screen told
 * him his ride had arrived. Being *near* the spot is deliberately not enough to set the claim —
 * only a tap does that, because parking where you always meet is more than being nearby.
 */
export function driverProgressAfterMove(progress: DriverProgress, metersFromPoint: number | null): DriverProgress {
	if (progress !== "at_pickup_point" || metersFromPoint == null) {
		return progress;
	}
	return metersFromPoint > PICKUP_POINT_METERS ? "driving" : progress;
}

export const PICKUP_STAGE_ORDER: PickupStage[] = ["stirring", "likely", "tentative", "pinned"];

/** Later is more certain. Used to decide whether news is worth telling. */
export function stageRank(stage: PickupStage): number {
	return PICKUP_STAGE_ORDER.indexOf(stage);
}

/**
 * Whether the fix puts him clearly outside the school fence.
 *
 * Subtracting the accuracy is the whole rule: a 1 km fix 200 m from school is not evidence of
 * anything, and treating it as a departure would raise the pickup while he is still in a lesson.
 */
export function hasLeftSchool(metersFromSchool: number | null, accuracyMeters: number | null): boolean {
	if (metersFromSchool == null) {
		return false;
	}
	return metersFromSchool - (accuracyMeters ?? 0) > SCHOOL_FENCE_METERS;
}

/** How sure we are, from where he is in his own journey. */
export function pickupStage(phase: RiderPhase | null, leftSchool: boolean, hasPin: boolean): PickupStage {
	if (phase === "on_train" || phase === "at_destination" || phase === "arrived" || (hasPin && phase === "on_platform")) {
		return "pinned";
	}
	if (phase === "at_station" || phase === "on_platform") {
		return "tentative";
	}
	return leftSchool ? "likely" : "stirring";
}

/**
 * Whether reaching this rung is worth buzzing the driver about.
 *
 * `tentative` and `pinned` both mean "he has a train", so announcing both says the same thing
 * twice. That is a rule about *rungs arriving on their own* — a guess narrowing, where the app is
 * deciding for itself how much to interrupt.
 *
 * A press is not a guess. Asking outright says something no rung does, at whichever rung he
 * happens to be standing on, and there is nothing to de-duplicate it against. Applying the quiet
 * rule to it meant a request made on the platform reached nobody.
 *
 * Here rather than in the Worker because it is a decision, and decisions are the part of this
 * repository that gets tested.
 */
export function announcesStage(stage: PickupStage, asked: boolean): boolean {
	return asked || stage !== "tentative";
}

/**
 * Where Eben is, for someone driving to meet him.
 *
 * His own phases with the two that matter to a driver split apart: "at school" and "left school"
 * are the same rider phase (`at_home` on the way back) but they are the difference between a
 * pickup worth thinking about and one worth leaving for.
 */
export const PICKUP_RIDER_STEPS = ["At school", "Left school", "At station", "On platform", "On train", "Off train"] as const;

export const PICKUP_ACK_LABELS: Record<PickupAckStep, { driver: string; rider: string }> = {
	school: { driver: "school", rider: "Knows you are at school" },
	shuttle_stop: { driver: "shuttle stop", rider: "Knows you are at the shuttle stop" },
	school_departure: { driver: "school departure", rider: "Knows you left school" },
	station: { driver: "station", rider: "Knows you are at the station" },
	platform: { driver: "platform", rider: "Knows you are on the platform" },
	train: { driver: "train", rider: "Knows you are on the train" },
	off_train: { driver: "off train", rider: "Knows you are off the train" },
};

export function pickupAckStep(pickup: Pick<PickupState, "riderPhase" | "stage" | "riderAtShuttleStop">): PickupAckStep | null {
	if (pickup.riderPhase === "at_destination" || pickup.riderPhase === "arrived") return "off_train";
	if (pickup.riderPhase === "on_train") return "train";
	if (pickup.riderPhase === "on_platform") return "platform";
	if (pickup.riderPhase === "at_station") return "station";
	if (pickup.stage === "likely") return "school_departure";
	if (pickup.stage === "stirring" && pickup.riderAtShuttleStop) return "shuttle_stop";
	if (pickup.stage === "stirring" && pickup.riderPhase === "at_home") return "school";
	return null;
}

export function riderStepIndex(phase: RiderPhase | null, stage: PickupStage): number {
	if (phase === "at_destination" || phase === "arrived") {
		return 5;
	}
	if (phase === "on_train") {
		return 4;
	}
	if (phase === "on_platform") {
		return 3;
	}
	if (phase === "at_station") {
		return 2;
	}
	// Before the station there is only one phase, so the fence is what tells the two apart.
	return stage === "stirring" ? 0 : 1;
}

/**
 * Who may say the pickup happened.
 *
 * The rider's own phase is the normal witness. A driver who has parked is the fallback, because
 * a phone that has stopped reporting must not be able to strand him at the kerb. Both arguments
 * are required: this is the only place the rule lives, and a caller that omits half of it would
 * be answering a different question.
 */
export function canConfirmPickup(phase: RiderPhase | null, driverProgress: DriverProgress): boolean {
	return phase === "at_destination" || phase === "arrived" || driverProgress === "at_pickup_point";
}

/**
 * The train's arrival, relative to now, without pretending a gone one is imminent.
 *
 * Clamping the countdown at zero is what makes an abandoned pickup read as urgent: a train that
 * arrived at ten to five still says "train in 0 min" at seven o'clock. Past its arrival the only
 * honest thing to report is how long ago it was.
 */
export function trainRelativeText(arrivalMs: number, nowMs: number): string {
	const minutes = Math.round((arrivalMs - nowMs) / 60_000);
	if (minutes > 0) {
		return `arrival in ${minutes} min`;
	}
	return minutes === 0 ? "arrival due now" : `gone ${-minutes} min ago`;
}

/**
 * Can he still reach a train leaving at this moment?
 *
 * Optimistic on purpose (see `APPROACH_SPEED_METERS_PER_SECOND`). With no idea where he is the
 * answer is yes, because the earlier train is the one worth planning against.
 */
export function canReach(departureMs: number | null, metersToStation: number | null, nowMs: number): boolean {
	if (departureMs == null) {
		return false;
	}
	if (metersToStation == null) {
		return departureMs > nowMs;
	}
	return (departureMs - nowMs) / 1000 >= metersToStation / APPROACH_SPEED_METERS_PER_SECOND;
}

export interface PickupChoice {
	stage: PickupStage;
	/** The train being met. Tentative until the stage is `pinned`. */
	train: PickupTrain | null;
	/** The one after it, so the slack on offer is visible rather than inferred. */
	next: PickupTrain | null;
}

/**
 * The upcoming arrivals at the pickup station.
 *
 * Deliberately keeps trains that have already left Rockridge but not yet reached the far end: he
 * may have caught one of those, and "you are too late to matter" is not a conclusion a timetable
 * can draw about a boy who might have run.
 */
export function pickupCandidates(
	options: CommutePlan[],
	destinationIndex: number,
	nowMs: number,
	// The train he is on stops being "upcoming" the moment it pulls in, and dropping it there is
	// what let the deck slide onto the next one while he was walking up to the kerb.
	pinnedPlanId: string | null = null,
	limit = DECK_CANDIDATES,
): PickupTrain[] {
	return options
		.map((plan): PickupTrain | null => {
			const stop = plan.destinations[destinationIndex] ?? plan.destinations[0] ?? null;
			return stop == null
				? null
				: {
						planId: plan.id,
						label: `${plan.routeName} to ${headsignLabel(plan.headsign)}`,
						departureMs: plan.boarding.predictedMs,
						arrivalMs: stop.predictedMs,
					};
		})
		.filter(
			(train): train is PickupTrain =>
				train != null && train.arrivalMs != null && (train.arrivalMs > nowMs || train.planId === pinnedPlanId),
		)
		.sort((a, b) => (a.arrivalMs ?? 0) - (b.arrivalMs ?? 0))
		.slice(0, limit);
}

/**
 * How many arrivals the rider's phone sends.
 *
 * Far more than the four the deck shows, because the deck *slides*: the likely train moves along
 * this list as the clock runs out on each one, and the list is only refreshed when Eben's phone
 * speaks. His phone sleeps for long stretches, so a short list quietly thins the deck to two or
 * three cards while nobody is looking. Ten covers well over an hour at this headway.
 */
const DECK_CANDIDATES = 10;

/**
 * The cards worth swiping through: one before the likely train, and two after.
 *
 * The one before is the point of the exercise — it is the train he might still make if he moves,
 * and it is the difference between leaving now and finishing your coffee. Once he has actually
 * boarded there is nothing left to choose, so the deck collapses to the train he is on.
 */
export function pickupDeck(candidates: PickupTrain[], likelyPlanId: string | null, pinnedPlanId: string | null): PickupTrain[] {
	if (pinnedPlanId) {
		const pinned = candidates.find((train) => train.planId === pinnedPlanId);
		return pinned ? [pinned] : candidates.slice(0, 1);
	}
	const index = Math.max(0, candidates.findIndex((train) => train.planId === likelyPlanId));
	return candidates.slice(Math.max(0, index - 1), index + 3);
}

/** True for a card earlier than the one the rules picked: reachable only if he hurries. */
export function isUnlikelyTrain(train: PickupTrain, candidates: PickupTrain[], likelyPlanId: string | null): boolean {
	const likely = candidates.findIndex((entry) => entry.planId === likelyPlanId);
	const here = candidates.findIndex((entry) => entry.planId === train.planId);
	return likely >= 0 && here >= 0 && here < likely;
}

/**
 * What a card is, said plainly.
 *
 * `missed` and `unlikely` are different claims and the difference matters: one is a train that
 * has physically left Rockridge, the other is one still sitting there that he probably cannot
 * reach. Only the first is settled.
 */
export type TrainStanding = "boarded" | "arrived" | "picked" | "missed" | "unlikely" | "likely" | "later";

export function trainStanding(
	train: PickupTrain,
	candidates: PickupTrain[],
	likelyPlanId: string | null,
	pinned: boolean,
	riderPhase: RiderPhase | null,
	nowMs: number,
): TrainStanding {
	if (pinned) {
		if (riderPhase === "at_destination" || riderPhase === "arrived") {
			return "arrived";
		}
		return riderPhase === "on_train" ? "boarded" : "picked";
	}
	if (train.departureMs != null && train.departureMs <= nowMs) {
		return "missed";
	}
	if (isUnlikelyTrain(train, candidates, likelyPlanId)) {
		return "unlikely";
	}
	return train.planId === likelyPlanId ? "likely" : "later";
}

export const TRAIN_STANDING_LABELS: Record<TrainStanding, string> = {
	boarded: "On the train",
	arrived: "Off the train",
	picked: "Selected train",
	missed: "Missed",
	unlikely: "Unlikely",
	likely: "Likely",
	later: "Later",
};

export const TRAIN_STANDING_TONES: Record<TrainStanding, string> = {
	boarded: "good",
	arrived: "good",
	picked: "good",
	missed: "bad",
	unlikely: "bad",
	likely: "warn",
	later: "info",
};

/** Once aboard, where he is now matters more to the driver than the fact that he boarded. */
export function trainStandingLabel(standing: TrainStanding, nextStopName: string | null): string {
	return standing === "boarded" && nextStopName ? stationLabel(nextStopName) : TRAIN_STANDING_LABELS[standing];
}

/** Where the rider stands in relation to one train on the private planning deck. */
export type PlanningStanding = "missed" | "unreachable" | "tight" | "next" | "option";

/**
 * Slack below which catching it means everything going right.
 *
 * A judgement, not a measurement, and deliberately small: the approach speed it is measured against
 * is already optimistic, so two minutes on top of that is the difference between a plan and a hope.
 */
const PLANNING_TIGHT_SECONDS = 120;

/**
 * The planning deck read from the rider's side.
 *
 * Planning happens while Eben is still at school — the shuttle stop is on campus, so one position
 * answers for both — and the question every card is really asking is "could he be on this one".
 * Without that the deck said nothing about the trains at all: every card wore the same pill naming
 * the mode, which David already knows he is in.
 *
 * Only ever `tight` on the earliest train he can reach. The ground to cover is the same for all of
 * them and the departures only get later, so slack grows down the list and a later card cannot be
 * the scramble. Which makes the ordering load-bearing: `trains` must be in departure order, as
 * `snapshot.options` is.
 *
 * Optimistic by construction, through `canReach` — this is a plan being made an hour out, and the
 * cost of being wrong is offering a train he then misses, not stranding him.
 */
export function planningStandings(
	trains: readonly { id: string; departureMs: number | null }[],
	metersToStation: number | null,
	nowMs: number,
): Record<string, PlanningStanding> {
	const earliest = trains.findIndex((train) => canReach(train.departureMs, metersToStation, nowMs));
	const approachSeconds = metersToStation == null ? 0 : metersToStation / APPROACH_SPEED_METERS_PER_SECOND;
	const standings: Record<string, PlanningStanding> = {};
	for (const [index, train] of trains.entries()) {
		if (train.departureMs != null && train.departureMs <= nowMs) {
			standings[train.id] = "missed";
		} else if (index !== earliest && !canReach(train.departureMs, metersToStation, nowMs)) {
			standings[train.id] = "unreachable";
		} else if (index !== earliest) {
			standings[train.id] = "option";
		} else {
			const slack = train.departureMs == null ? 0 : (train.departureMs - nowMs) / 1000 - approachSeconds;
			standings[train.id] = slack < PLANNING_TIGHT_SECONDS ? "tight" : "next";
		}
	}
	return standings;
}

/** `WILL MISS` rather than `MISSED`: one has gone, the other is one he cannot get to in time. */
export const PLANNING_STANDING_LABELS: Record<PlanningStanding, string> = {
	missed: "MISSED",
	unreachable: "WILL MISS",
	tight: "TIGHT",
	next: "NEXT",
	option: "OPTION",
};

export const PLANNING_STANDING_TONES: Record<PlanningStanding, string> = {
	missed: "bad",
	unreachable: "bad",
	tight: "warn",
	next: "good",
	option: "info",
};

/**
 * The earliest train he can still reach.
 *
 * Deliberately re-decidable from nothing but the candidates, how far he was, and the clock — so
 * the driver's screen can keep asking it every second rather than believing an answer worked out
 * when Eben's phone last spoke. Sitting still for four minutes turns a likely train into a missed
 * one, and the card has to notice.
 */
export function likelyTrainId(candidates: PickupTrain[], metersToStation: number | null, nowMs: number): string | null {
	const index = candidates.findIndex((train) => canReach(train.departureMs, metersToStation, nowMs));
	return (index >= 0 ? candidates[index] : candidates[0])?.planId ?? null;
}

/**
 * Which train the pickup is about.
 *
 * Once he has pinned one, that is the answer and nothing here second-guesses it — the pin is his
 * to set (he is the one on the platform) and the deck on David's phone is a readout. Before then
 * it is the first departure he can still reach, which with no position at all is simply the next.
 */
export function choosePickupTrain(
	report: Pick<PickupReport, "candidates" | "pinnedPlanId">,
	stage: PickupStage,
	metersToStation: number | null,
	nowMs: number,
): PickupChoice {
	const candidates = report.candidates.filter((train) => train.arrivalMs != null);
	if (stage === "pinned" && report.pinnedPlanId) {
		const pinned = candidates.find((train) => train.planId === report.pinnedPlanId) ?? null;
		if (pinned) {
			return { stage, train: pinned, next: candidates[candidates.indexOf(pinned) + 1] ?? null };
		}
		/*
		 * Aboard, with his train no longer on the deck.
		 *
		 * Naming the next one instead is how the card came to read "Eben in 13 minutes" while the
		 * line above it said he had been off the train for 28 seconds. Saying nothing about the
		 * train leaves the stored one standing, which is the one he actually caught.
		 */
		return { stage, train: null, next: candidates[0] ?? null };
	}
	// Before he has a pin the earliest thing he can still reach is the answer.
	const likelyId = likelyTrainId(candidates, metersToStation, nowMs);
	const chosen = Math.max(0, candidates.findIndex((train) => train.planId === likelyId));
	return { stage, train: candidates[chosen] ?? null, next: candidates[chosen + 1] ?? null };
}

export interface PickupTimingInput {
	/** When his train reaches the pickup station. */
	trainArrivalMs: number | null;
	/** Traffic-aware drive to the curb, for working out when to set off. */
	driveSeconds: number | null;
	prepSeconds: number;
	/** When David is expected at the curb, if he is already under way. */
	driverArrivalMs: number | null;
	nowMs: number;
}

export interface PickupTiming {
	/** Train arrival plus the walk up from the platform: when Eben is actually at the curb. */
	riderReadyMs: number | null;
	driverArrivalMs: number | null;
	/** Positive is late, negative is time to spare. */
	deltaSeconds: number | null;
	leaveByMs: number | null;
	startByMs: number | null;
	/** How long Eben stands there, never negative — David being early is not a wait. */
	waitSeconds: number | null;
}

export function pickupTiming(input: PickupTimingInput): PickupTiming {
	const riderReadyMs = input.trainArrivalMs == null ? null : input.trainArrivalMs + PLATFORM_TO_CURB_SECONDS * 1000;
	const driverArrivalMs =
		input.driverArrivalMs ?? (input.driveSeconds == null ? null : input.nowMs + input.driveSeconds * 1000);
	const leaveByMs = riderReadyMs == null || input.driveSeconds == null ? null : riderReadyMs - input.driveSeconds * 1000;
	return {
		riderReadyMs,
		driverArrivalMs,
		deltaSeconds: riderReadyMs == null || driverArrivalMs == null ? null : Math.round((driverArrivalMs - riderReadyMs) / 1000),
		leaveByMs,
		startByMs: leaveByMs == null ? null : leaveByMs - input.prepSeconds * 1000,
		waitSeconds:
			riderReadyMs == null || driverArrivalMs == null ? null : Math.max(0, Math.round((driverArrivalMs - riderReadyMs) / 1000)),
	};
}

/**
 * The same thing again, from the record both phones read rather than from loose figures.
 *
 * The `preparing` branch is the subtle part and is why this is shared: before David sets off, the
 * stored arrival means "if I left now", so it has to be unpacked back into a drive length. Once he
 * is moving it is an actual arrival time and using it as a duration would restart the drive on
 * every render.
 */
export function pickupTimingFor(
	pickup: Pick<PickupState, "train" | "driverArrivalMs" | "driverSeenAtMs" | "driverProgress">,
	prepSeconds: number,
	nowMs: number,
	train: PickupTrain = pickup.train,
): PickupTiming {
	const preparing = pickup.driverProgress === "preparing";
	const driveSeconds =
		pickup.driverArrivalMs == null || pickup.driverSeenAtMs == null
			? null
			: Math.round((pickup.driverArrivalMs - pickup.driverSeenAtMs) / 1000);
	return pickupTiming({
		trainArrivalMs: train.arrivalMs,
		driveSeconds: preparing ? driveSeconds : null,
		prepSeconds,
		driverArrivalMs: preparing ? null : pickup.driverArrivalMs,
		nowMs,
	});
}

/**
 * How much to trust a figure whose phone has gone quiet.
 *
 * iOS freezes a backgrounded PWA, so silence is the ordinary condition rather than a fault —
 * which is exactly why it has to be shown rather than hidden. The tiers escalate so a projection
 * running for several minutes cannot pass for a live reading.
 */
export type Freshness = "live" | "recent" | "stale" | "cold";

export function freshnessOf(seenAtMs: number | null, nowMs: number): Freshness {
	if (seenAtMs == null) {
		return "cold";
	}
	const ageMs = nowMs - seenAtMs;
	if (ageMs < 60_000) {
		return "live";
	}
	if (ageMs < 3 * 60_000) {
		return "recent";
	}
	return ageMs < 10 * 60_000 ? "stale" : "cold";
}

/** Said only when it is worth saying: a reading a few seconds old needs no apology. */
export function freshnessNote(freshness: Freshness, seenAtMs: number | null, nowMs: number): string | null {
	if (freshness === "live" || seenAtMs == null) {
		return null;
	}
	const minutes = Math.max(1, Math.round((nowMs - seenAtMs) / 60_000));
	return freshness === "cold" ? `no update for ${minutes} min` : `${minutes} min old`;
}

export type PickupTone = "wait" | "go" | "drive" | "here" | "done";

export interface PickupCard {
	/** Small word above the number, e.g. "LEAVE IN". Null when the hero is itself a verb. */
	lead: string | null;
	hero: string;
	unit: string | null;
	sub: string | null;
	/** The second line: time to spare, or how late this is running. */
	note: { value: string; label: string; tone: "good" | "warn" } | null;
	tone: PickupTone;
}

function minutesFrom(ms: number): number {
	return Math.max(0, Math.round(ms / 60_000));
}

/** "2 min to spare" / "3 min late" — the wording the leave card already uses, both ways round. */
export function deltaText(deltaSeconds: number | null): string | null {
	if (deltaSeconds == null) {
		return null;
	}
	const minutes = Math.round(Math.abs(deltaSeconds) / 60);
	if (minutes === 0) {
		return "right on time";
	}
	return deltaSeconds < 0 ? `${minutes} min to spare` : `${minutes} min late`;
}

function minuteWord(value: number): string {
	return Math.abs(value) === 1 ? "minute" : "minutes";
}

/** The margin, in the shape the card's second line already uses: a number and its label. */
export function pickupNote(deltaSeconds: number | null): PickupCard["note"] {
	if (deltaSeconds == null) {
		return null;
	}
	const minutes = Math.round(Math.abs(deltaSeconds) / 60);
	return deltaSeconds > 0
		? { value: `${minutes}`, label: `${minuteWord(minutes)} late`, tone: "warn" }
		: { value: `${minutes}`, label: `${minuteWord(minutes)} to spare`, tone: "good" };
}

/**
 * The same margin, for a card that is still counting down to the leave moment.
 *
 * Before he goes, use the fixed margin promised by the plan. Once the leave moment arrives, use
 * the live margin so delay on the road is visible.
 */
export function planningNote(timing: PickupTiming, nowMs: number): PickupCard["note"] {
	if (timing.startByMs != null && timing.leaveByMs != null && nowMs < timing.startByMs) {
		const note = pickupNote((timing.startByMs - timing.leaveByMs) / 1000);
		return note == null ? null : { ...note, label: `${note.label} at departure` };
	}
	return pickupNote(timing.deltaSeconds);
}

/**
 * The one dominant line on David's card.
 *
 * Built as lead / hero / unit rather than one sentence, because that is what every other card in
 * the app is: a small label, one big number, and the word for what the number counts. The number
 * is always a countdown — an absolute time has to be subtracted from the clock before it means
 * anything, and this card is read at a glance.
 *
 * Ordered by what David is doing rather than by what the train is doing: once he is driving the
 * question stops being "when do I go" and becomes "am I going to make it".
 */
export function pickupCard(progress: DriverProgress, timing: PickupTiming, nowMs: number, trainAt: string | null = null): PickupCard {
	const note = pickupNote(timing.deltaSeconds);
	if (progress === "picked_up") {
		return { lead: null, hero: "PICKED UP", unit: null, sub: null, note: null, tone: "done" };
	}
	if (progress === "at_pickup_point") {
		const waitingMs = timing.riderReadyMs == null ? null : timing.riderReadyMs - nowMs;
		if (waitingMs == null) {
			return { lead: null, hero: "WAITING", unit: null, sub: "parked and waiting", note: null, tone: "here" };
		}
		if (waitingMs <= 0) {
			return { lead: null, hero: "RIDER IS DUE", unit: null, sub: "any moment now", note: null, tone: "here" };
		}
		const minutes = minutesFrom(waitingMs);
		return { lead: "RIDER IN", hero: `${minutes}`, unit: minuteWord(minutes), sub: "parked and waiting", note: null, tone: "here" };
	}
	if (progress === "driving") {
		if (timing.driverArrivalMs == null) {
			return { lead: null, hero: "DRIVING", unit: null, sub: null, note, tone: "drive" };
		}
		const minutes = minutesFrom(timing.driverArrivalMs - nowMs);
		return { lead: "THERE IN", hero: `${minutes}`, unit: minuteWord(minutes), sub: "on your way to the curb", note, tone: "drive" };
	}
	if (timing.startByMs == null) {
		return { lead: null, hero: "PICKUP", unit: null, sub: "working out the drive", note: null, tone: "wait" };
	}
	const sub = trainAt ? `for the ${trainAt} arrival` : null;
	// Rounded, not exact: "leave in 0 minutes" is a countdown that has finished but will not say so.
	const minutes = minutesFrom(timing.startByMs - nowMs);
	if (nowMs >= timing.startByMs || minutes <= 0) {
		return { lead: null, hero: "LEAVE NOW", unit: null, sub, note, tone: "go" };
	}
	return { lead: "LEAVE IN", hero: `${minutes}`, unit: minuteWord(minutes), sub, note: planningNote(timing, nowMs), tone: "wait" };
}

export function riderToldLine(told: PickupStage | null, atShuttleStop = false): string {
	if (atShuttleStop) {
		return "Shuttle-stop update sent";
	}
	if (told === "pinned") {
		return "Train update sent";
	}
	if (told === "likely") {
		return "School-departure update sent";
	}
	// Only reachable by asking outright: the fence never announces this rung on its own.
	if (told === "tentative") {
		return "Station update sent";
	}
	return told === "stirring" ? "Heading-home update sent" : "Contacting driver…";
}

/**
 * Eben's line, which never names David.
 *
 * "Your ride" rather than a name: it reads better on his screen and it keeps the copy from
 * hard-coding a person the way the model underneath deliberately does.
 *
 * `paused` is David having left the screen. Deliberately not the same as no pickup at all — the
 * meeting still stands, so the line stays and only the figures go to a dash.
 */
export function riderPickupLine(
	kind: "offered" | "paused" | "active" | "completed",
	progress: DriverProgress,
	timing: PickupTiming,
	meters: number | null,
	nowMs: number,
	told: PickupStage | null = null,
	atShuttleStop = false,
): { status: string; wait: string; tone: "good" | "warn" | "info" } {
	if (kind === "completed" || progress === "picked_up") {
		return { status: "Picked up", wait: "", tone: "good" };
	}
	// "Pickup request sent" said only that the app had tried. What he asked for is whether it landed.
	if (kind === "offered") {
		return { status: riderToldLine(told, atShuttleStop), wait: "", tone: "info" };
	}
	// Before the paused check: being parked at the curb is a fact, and it does not stop being true
	// because David closed the screen. Only his live figures do, which is what `paused` withholds.
	//
	// No wait line either — "No wait, your ride is here" under "Your ride is here" reads as somebody
	// correcting themselves mid-sentence.
	if (progress === "at_pickup_point") {
		return { status: "Your ride is here", wait: "", tone: "good" };
	}
	/*
	 * Not on the way yet, and saying otherwise is the difference between waiting patiently and
	 * walking to the kerb. `preparing` is David with the app open and the car still parked.
	 */
	if (progress === "not_started" || progress === "preparing") {
		const late = timing.waitSeconds != null && timing.waitSeconds < 0;
		return { status: late ? "Your ride has not left yet" : "Your ride has not set off yet", wait: "", tone: late ? "warn" : "info" };
	}
	if (kind === "paused") {
		return { status: "Your ride is coming · —", wait: "", tone: "info" };
	}
	const away = timing.driverArrivalMs == null ? null : minutesFrom(timing.driverArrivalMs - nowMs);
	const distance = meters == null ? null : meters >= 400 ? `${(meters / 1609.344).toFixed(1)} mi` : `${meters} m`;
	// "20 min" beside a distance reads as another measurement. "20 min away" is the sentence.
	const parts = ["Your ride is coming", away == null ? null : `${away} min away`, distance].filter((part): part is string => part != null);
	const wait = timing.waitSeconds == null ? "" : timing.waitSeconds < 60 ? "No wait" : `About ${Math.round(timing.waitSeconds / 60)} min wait`;
	// Negative slack is David arriving after the train does, which is the one case worth colouring.
	return { status: parts.join(" · "), wait, tone: timing.waitSeconds != null && timing.waitSeconds < 0 ? "warn" : "good" };
}
